/*
  The lesson detail sheet. Mirrors the portal's timetable popover (CustomEvent BottomSheetContent):
  day and time, class tag, teacher, Zoom link; plus copy buttons, the recording and a reminder note.
*/
import { CopyOutlined, PlayCircleOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { Drawer, message } from "antd";
import Badge from "app/components/Badge";
import SubjectIcon from "app/components/Icons/SubjectIcon";
import { Button } from "app/components/v1/Button";
import { openExternal } from "app/native/nativeApp";
import type { Child, Lesson, ReminderPrefs } from "app/services/types";
import { fmtDayLong, fmtTime, lessonPhase } from "app/utils/time";

export function studentNamesFor(lesson: Lesson, children: Child[]) {
  const byId = new Map(children.map((c) => [c.id, c]));
  return lesson.students.filter((s) => byId.has(s.id) && !s.onLeave).map((s) => byId.get(s.id)!.name);
}

const copy = async (label: string, value: string) => {
  try { await navigator.clipboard.writeText(value); message.success(`${label} copied`); } catch { message.info(value); }
};

export default function LessonSheet({ lesson, children, prefs, onClose }: { lesson: Lesson | null; children: Child[]; prefs: ReminderPrefs; onClose: () => void }) {
  if (!lesson) return <Drawer open={false} placement="bottom" onClose={onClose} />;
  const phase = lessonPhase(lesson.start, lesson.end);
  const names = studentNamesFor(lesson, children);
  const canJoin = Boolean(lesson.zoomLink && lesson.zoomMeetingId && lesson.zoomMeetingPasscode) && !lesson.cancelled && phase !== "past";
  return (
    <Drawer open placement="bottom" height="auto" onClose={onClose} closable={false} styles={{ body: { padding: 20, paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }, wrapper: { borderRadius: "20px 20px 0 0", overflow: "hidden" } }}>
      <div className="d-flex gap-3 align-items-start">
        <SubjectIcon name={lesson.subject} imageUrl={lesson.subjectImage} style={{ width: 56, height: 56 }} />
        <div className="flex-grow-1">
          <h4 className="mb-1" style={{ fontSize: 22 }}>{lesson.year ? `${lesson.year} ` : ""}{lesson.subject}</h4>
          <div className="ct-muted" style={{ fontWeight: 600 }}>{fmtDayLong(lesson.start)} · {fmtTime(lesson.start)} - {fmtTime(lesson.end)}</div>
        </div>
      </div>
      <div className="d-flex gap-2 flex-wrap mt-3">
        <Badge status="primary" text={lesson.classId} capitalize={false} />
        {lesson.teacher && <Badge status="muted" text={`Teacher ${lesson.teacher}`} capitalize={false} />}
        {names.length > 0 && <Badge status="success" text={names.join(", ")} capitalize={false} />}
        {lesson.cancelled && <Badge status="danger" text="Cancelled" />}
        {phase === "live" && !lesson.cancelled && <Badge status="success" text="Happening now" />}
      </div>

      {lesson.cancelled ? (
        <p className="mt-3 mb-0 ct-muted">This lesson has been cancelled. If you have a question, message the team.</p>
      ) : phase === "past" ? (
        <div className="mt-3">
          {lesson.recordingLink ? <Button className="w-100" icon={<PlayCircleOutlined />} name="Watch the recording" onClick={() => openExternal(lesson.recordingLink!)} /> : <p className="mb-0 ct-muted">The recording appears here once it is uploaded.</p>}
        </div>
      ) : (
        <div className="mt-3">
          {canJoin ? (
            <>
              <Button className="w-100" size="lg" icon={<VideoCameraOutlined />} name="Join on Zoom" onClick={() => openExternal(lesson.zoomLink!)} />
              <div className="d-flex gap-2 mt-2">
                <Button className="flex-grow-1" buttonType="outlined" color="secondary" size="sm" icon={<CopyOutlined />} name={`ID ${lesson.zoomMeetingId}`} onClick={() => copy("Meeting ID", lesson.zoomMeetingId!)} />
                <Button className="flex-grow-1" buttonType="outlined" color="secondary" size="sm" icon={<CopyOutlined />} style={{ textTransform: "none" }} name={`Passcode ${lesson.zoomMeetingPasscode}`} onClick={() => copy("Passcode", lesson.zoomMeetingPasscode!)} />
              </div>
            </>
          ) : (
            <p className="mb-0 ct-muted">The Zoom link will appear here shortly before the lesson.</p>
          )}
          <p className="mt-3 mb-0 ct-muted" style={{ fontSize: 13 }}>
            {prefs.enabled ? `You will be reminded ${prefs.minutesBefore === 0 ? "when it starts" : `${prefs.minutesBefore} minutes before`} (change this in Settings).` : "Lesson reminders are off. Turn them on in Settings."}
          </p>
        </div>
      )}
    </Drawer>
  );
}
